import { DocumentType } from '@synergy/models';
import { PoolDropzone, PoolLinearProgress, PoolTypography } from '@synergy/components';
import { Theme } from '@mui/material';
import { downloadUrl } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import urljoin from 'url-join';

import * as ActivityActions from '../../../../../common/actions/activity-actions';
import * as ContactDetailDocumentsActions from './store/contact-detail-documents-actions';
import * as ContactDetailDocumentsSelectors from './store/contact-detail-documents-selectors';
import { ContactDetailDocumentsEditTitleModal } from './components/contact-detail-documents-edit-title-modal';
import { ContactDocument } from './models/contact-detail-document';
import { DocumentsTable } from '../../../components/documents/documents-table';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    progress: {
        marginTop: theme.spacing(2),
    },
    progressPlaceholder: {
        height: 4,
    },
    dropzone: {
        height: 160,
    },
    sectionContainer: {
        marginTop: theme.spacing(6),
    },
}));

export const ContactDetailDocuments = () => {
    const dispatch = useThunkDispatch();
    const { id: contactIdParam } = useParams<{ id: string }>();
    const classes = useStyles();
    const [selectedDocument, setSelectedDocument] = useState<ContactDocument | null>(null);

    const documents = useAppSelector(ContactDetailDocumentsSelectors.documentsSelector);
    const progress = useAppSelector(ContactDetailDocumentsSelectors.overallUploadProgressSelector);
    const documentBaseUrl = useAppSelector(state => state.settings.privateFilesystemPath);

    useEffect(() => {
        if (contactIdParam && +contactIdParam) {
            dispatch(ContactDetailDocumentsActions.fetchContactDocuments(+contactIdParam));
        }
    }, [contactIdParam, dispatch]);

    const handleEditTitleStart = (document: ContactDocument) => {
        setSelectedDocument(document);
    };

    const handleEditTitleComplete = (name: string) => {
        if (!selectedDocument) {
            return;
        }
        dispatch(ContactDetailDocumentsActions.renameDocument(selectedDocument.id, name));
        setSelectedDocument(null);
    };

    const handleDownload = (url: string, documentname: string) => {
        downloadUrl(urljoin(documentBaseUrl, url), documentname);
    };

    const handleDelete = async (document: ContactDocument) => {
        await dispatch(ContactDetailDocumentsActions.deleteDocument(document.id));
        if (contactIdParam) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Contact, +contactIdParam));
        }
    };

    const handleDrop = async (acceptedFiles: File[]) => {
        const deduplicatedDocuments = acceptedFiles.filter(
            file => !documents.some(doc => doc.originalName === file.name && doc.size === file.size)
        );
        if (deduplicatedDocuments.length > 0) {
            await dispatch(ContactDetailDocumentsActions.addContactDocuments(deduplicatedDocuments));
            if (contactIdParam) {
                dispatch(ActivityActions.fetchActivities(DocumentType.Contact, +contactIdParam));
            }
        }
    };

    return (
        <div>
            <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.documents.upload" />
            <PoolDropzone onDrop={handleDrop} className={classes.dropzone} />
            {progress ? (
                <PoolLinearProgress variant="determinate" value={progress} className={classes.progress} />
            ) : (
                <div className={`${classes.progress} ${classes.progressPlaceholder}`} />
            )}
            {documents && documents.length > 0 && (
                <div className={classes.sectionContainer}>
                    <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.documents.files" />
                    <DocumentsTable
                        documents={documents}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onEditTitle={handleEditTitleStart}
                    />
                </div>
            )}
            <ContactDetailDocumentsEditTitleModal
                document={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                onConfirm={handleEditTitleComplete}
            />
        </div>
    );
};
